/**
 * CodeField - Code editor with syntax highlighting and token drag-and-drop
 *
 * Uses CodeMirror 6 with language support for JavaScript, JSON, and plaintext.
 * Integrates with Fluent UI theme tokens for consistent styling.
 *
 * For JavaScript language, supports drag-and-drop of dynamic content tokens
 * which are converted to workflowContext expressions (matching LogicAppsUX behavior).
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Label,
  Text,
} from '@coreai/fluentui-react';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { useAppStore } from '@/stores/appStore';
import { createFluentTheme, formatJsonKeymap, wrapSelectionInputHandler } from '@/components/WorkflowDesigner/CodeEditor/extensions';
import { createJavaScriptAutocomplete, type WorkflowActionInfo } from '@/components/WorkflowDesigner/CodeEditor/extensions/jsAutocomplete';
import type { ValueSegment, Token } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  editorContainer: {
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflow: 'hidden',
    // Fill available vertical space: viewport height minus estimated header/tabs/label (~280px)
    minHeight: 'max(300px, calc(100vh - 280px))',
    '& .cm-editor': {
      height: '100%',
    },
    '& .cm-scroller': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase300,
    },
  },
  editorError: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
  },
  editorDisabled: {
    opacity: 0.6,
    pointerEvents: 'none',
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  editorDragOver: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: `inset 0 0 0 1px ${tokens.colorBrandStroke1}`,
  },
});

/**
 * Check if a string is a valid JavaScript identifier (safe for dot-notation property access).
 */
function isValidJsIdentifier(name: string): boolean {
  return /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name);
}

/**
 * Produce a property access suffix using dot notation when the key is a valid JS identifier,
 * or bracket notation with a single-quoted, escaped string otherwise.
 *
 * Examples:
 *   "HTTP"      → ".HTTP"
 *   "My-Action" → "['My-Action']"
 *   "it's"      → "[\"it\\'s\"]"
 */
function jsPropertyAccess(key: string): string {
  if (isValidJsIdentifier(key)) {
    return `.${key}`;
  }
  const escaped = key.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `['${escaped}']`;
}

/**
 * Convert a dynamic content token to a JavaScript expression using workflowContext.
 * Matches LogicAppsUX behavior where tokens become property accesses on the
 * workflowContext object available to inline JavaScript code.
 *
 * Uses bracket notation for any path segment that is not a valid JS identifier
 * (e.g., action names containing hyphens like "My-Action").
 *
 * Examples:
 *   outputs token from "HTTP" action, body     → workflowContext.actions.HTTP.outputs.body
 *   outputs token from "My-Action" action, body → workflowContext.actions['My-Action'].outputs.body
 *   outputs token from trigger, body.id        → workflowContext.trigger.outputs.body.id
 *   variable token "myVar"                     → workflowContext.trigger.outputs  (fallback)
 */
export function formatTokenForJavaScript(token: Token): string {
  const actionName = token.actionName;
  const source = token.source ?? 'body';
  const name = token.name ?? '';

  // Determine if this is a trigger or action token
  const category = actionName ? 'actions' : 'trigger';

  let result = `workflowContext.${category}`;

  if (actionName) {
    result = `${result}${jsPropertyAccess(actionName)}`;
  }

  // Append the source path
  if (source === 'outputs') {
    result = `${result}.outputs`;
  } else {
    result = `${result}.outputs${jsPropertyAccess(source)}`;
  }

  // Append the property name if present
  if (name && name !== source && name !== 'body') {
    result = `${result}${jsPropertyAccess(name)}`;
  }

  return result;
}

/** Supported code languages - language is specified in manifest x-ms-editor-options */
export type CodeLanguage = 'javascript' | 'json' | 'python' | 'plaintext';

export interface CodeFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Code language for syntax highlighting (from manifest x-ms-editor-options.language) */
  language?: CodeLanguage | undefined;
  /** Minimum height in pixels */
  minHeight?: number | undefined;
  /** Maximum height in pixels */
  maxHeight?: number | undefined;
  /** Read-only mode */
  readOnly?: boolean | undefined;
  /** Workflow actions/triggers for workflowContext IntelliSense */
  workflowActions?: WorkflowActionInfo[] | undefined;
}

/**
 * Get CodeMirror language extension for a given language.
 */
function getLanguageExtension(language: CodeLanguage): Extension {
  switch (language) {
    case 'javascript':
      return javascript();
    case 'json':
      return json();
    case 'python':
    case 'plaintext':
    default:
      // No syntax highlighting for these languages (could add @codemirror/lang-python later)
      return [];
  }
}

export const CodeField = memo(({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  language = 'javascript',
  readOnly = false,
  workflowActions,
}: CodeFieldProps) => {
  const styles = useStyles();
  const theme = useAppStore((s) => s.theme);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  const languageCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());
  const autocompleteCompartment = useRef(new Compartment());
  const isUpdatingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [isDragOver, setIsDragOver] = useState(false);

  // Handle token drag-and-drop into the code editor
  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation(); // Prevent ParameterField's drag handler from also activating
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, [disabled, readOnly]);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent ParameterField's drop handler from also inserting the token
    setIsDragOver(false);
    if (disabled || readOnly || !viewRef.current) return;

    const jsonData = e.dataTransfer.getData('application/json');
    if (!jsonData) return;

    try {
      const token = JSON.parse(jsonData) as Token;
      // For JavaScript, convert token to workflowContext expression
      const insertText = language === 'javascript'
        ? formatTokenForJavaScript(token)
        : (token.value ?? token.title);

      // Insert at the current cursor position in CodeMirror
      const view = viewRef.current;
      const cursor = view.state.selection.main.head;
      view.dispatch({
        changes: { from: cursor, insert: insertText },
        selection: { anchor: cursor + insertText.length },
      });
      view.focus();
    } catch {
      // Ignore invalid JSON
    }
  }, [disabled, readOnly, language]);

  // Get string value from value prop
  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : (value || '');

  // Create editor on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      foldGutter({
        openText: '▼',
        closedText: '▶',
      }),
      bracketMatching(),
      wrapSelectionInputHandler(),
      closeBrackets(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      search({ top: true }),
      EditorState.tabSize.of(2),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...searchKeymap,
        // Format Document (Shift+Alt+F) — only for JSON. Other languages
        // would need a real formatter (prettier, black, etc.) to format safely.
        ...(language === 'json' ? formatJsonKeymap : []),
        indentWithTab,
      ]),
      // Intercept native drop events inside CodeMirror to prevent it from
      // inserting token text/plain data. Our React onDrop handler on the
      // container div handles token drops with workflowContext formatting.
      EditorView.domEventHandlers({
        drop(event) {
          const jsonData = event.dataTransfer?.getData('application/json');
          if (jsonData) {
            // This is a token drop — prevent CodeMirror's native text insertion
            event.preventDefault();
            return true;
          }
          return false;
        },
      }),
      themeCompartment.current.of(createFluentTheme(theme)),
      languageCompartment.current.of(getLanguageExtension(language)),
      readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly || disabled)),
      // Add JavaScript IntelliSense via a Compartment so it can be reconfigured
      // when language or workflowActions change (removed when not javascript).
      autocompleteCompartment.current.of(
        language === 'javascript' ? createJavaScriptAutocomplete(workflowActions ?? []) : []
      ),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !isUpdatingRef.current) {
          const newValue = update.state.doc.toString();
          onChangeRef.current([{
            id: generateSegmentId(),
            type: ValueSegmentType.LITERAL,
            value: newValue,
          }]);
        }
      }),
    ];

    const state = EditorState.create({
      doc: stringValue,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update theme when it changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: themeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  // Update read-only state when it changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: readOnlyCompartment.current.reconfigure(
        EditorState.readOnly.of(readOnly || disabled)
      ),
    });
  }, [readOnly, disabled]);

  // Sync value from props to editor
  const setValue = useCallback((newValue: string) => {
    if (!viewRef.current) return;
    const currentValue = viewRef.current.state.doc.toString();
    if (currentValue === newValue) return;

    isUpdatingRef.current = true;
    viewRef.current.dispatch({
      changes: {
        from: 0,
        to: viewRef.current.state.doc.length,
        insert: newValue,
      },
    });
    isUpdatingRef.current = false;
  }, []);

  // Update editor when value prop changes
  useEffect(() => {
    setValue(stringValue);
  }, [stringValue, setValue]);

  // Update language when it changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: languageCompartment.current.reconfigure(getLanguageExtension(language)),
    });
  }, [language]);

  // Update JavaScript autocomplete when language or workflowActions change.
  // Removes the extension entirely for non-JavaScript languages.
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: autocompleteCompartment.current.reconfigure(
        language === 'javascript' ? createJavaScriptAutocomplete(workflowActions ?? []) : []
      ),
    });
  }, [language, workflowActions]);

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
      </div>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      <div
        ref={containerRef}
        className={mergeClasses(
          styles.editorContainer,
          error && styles.editorError,
          disabled && styles.editorDisabled,
          isDragOver && styles.editorDragOver
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      />

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
});

CodeField.displayName = 'CodeField';
